
import { makeStyles } from 'tss-react/mui';
import dayjs from 'dayjs';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { MutationStatus } from 'react-query';
import {
  ApiButton,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  nameRegExp,
  SingleSelectionRadio,
  TextFieldForm,
  Typography,
} from 'web-library';
import { IOptionType } from '../../../../api/candidate/exam/model';
import ControlledAutocomplete from '../../../../components/form-items/ControlledAutocomplete';
import { SiblingFormData } from '../../../../models';
import { useFormSibling } from '../../logic/useFormSibling';
import ControlledDatePicker from "web-library/stories/atom/DatePicker/ControlledDatePicker";

const NAME_TEXT = 'exam->>NonNationalExam.Name';

const useStyles = makeStyles()((theme) =>
({
  radioButton: {
    padding: '0px',
    margin: '0px',
  },
  backButton: {
    marginRight: theme.spacing(2),
  },
  buttonContainer: {
    marginTop: theme.spacing(3),
    marginRight: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  addIcon: {
    verticalAlign: 'sub',
    marginRight: 10,
    color: theme.color.icon.iconInverse,
  },
  selectSubjectText: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  customSelect: {
    marginTop: theme.spacing(3),
  },
  divider: {
    marginTop: theme.spacing(5),
  },
  input: {
    marginTop: theme.spacing(4),
  },
  gender: {
    color: theme.color.text.textHeader,
    marginTop: theme.spacing(4),
  },
  errorText: {
    color: theme.color.state.stateError,
  },
}));

interface DialogButtonsProps {
  onDialogClose: () => void;
  apiButtonClick: () => void;
  apiButtonDisabled: boolean;
  apiButtonStatus: MutationStatus;
}

const DialogButtons = ({
  apiButtonDisabled,
  onDialogClose,
  apiButtonStatus,
  apiButtonClick,
}: DialogButtonsProps) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  return (
    <DialogActions id="candidate-dialog-action">
      <Box className={classes.buttonContainer}>
        <Button onClick={onDialogClose} variant="text">
          {t('specialConsideration->>Dialog.Cancel')}
        </Button>
        <ApiButton status={apiButtonStatus} onClick={apiButtonClick} disabled={apiButtonDisabled}>
          {t('components->>IconLabel.SaveChanges')}
        </ApiButton>
      </Box>
    </DialogActions>
  );
};

const DialogTitleContainer = () => {
  const { t } = useTranslation();
  return (
    <DialogTitle>
      <Typography variant="h4">{t('exam->>NonNationalExam.EditSibling')}</Typography>
    </DialogTitle>
  );
};

interface EditSiblingDialogProps {
  modalState: boolean;
  onDialogClose: () => void;
  onUpdateDataTable: (updateValue: SiblingFormData) => void;
  siblingSelected: SiblingFormData | undefined;
  levels: IOptionType[] | undefined;
}

const EditSiblingDialog = ({
  modalState,
  onDialogClose,
  levels,
  onUpdateDataTable,
  siblingSelected,
}: EditSiblingDialogProps) => {
  const { t } = useTranslation();
  const { classes } = useStyles();

  const { formHook } = useFormSibling({ siblingSelected });
  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    getValues,
    reset,
    watch,
    trigger,
  } = formHook;
  const { gender } = getValues();
  const { dateOfBirth: dateOfBirthWatch } = watch();

  useEffect(() => {
    reset({ ...siblingSelected });
  }, [reset, siblingSelected]);

  const apiButtonClick = () => {
    handleSubmit((formData) => {
      onUpdateDataTable(formData);
      onDialogClose();
    })();
  };

  return (
    <Dialog open={modalState} maxWidth="md" onClose={onDialogClose}>
      <DialogTitleContainer />
      <DialogContent>
        <TextFieldForm
          fullWidth
          variant="outlined"
          id="name"
          name="name"
          error={!!errors?.name}
          helperText={errors.name?.message?.toString()}
          title={t(NAME_TEXT)}
          placeholder={t('exam->>PlaceHolders.EnterName')}
          label={t(NAME_TEXT)}
          form={formHook}
          isRequired
          regExpToBlock={nameRegExp}
        />
        <Typography variant="h6" className={classes.gender}>
          {t('exam->>NonNationalExam.Gender')}
        </Typography>
        <SingleSelectionRadio
          name="gender"
          color="primary"
          value={gender}
          onChange={(event) => {
            setValue('gender', event.target.value);
          }}
          data={[
            { label: t('exam->>NonNationalExam.Male'), value: 'Male' },
            { label: t('exam->>NonNationalExam.Female'), value: 'Female' },
          ]}
          control={control}
        />
        {errors.gender?.message && !gender && (
          <Typography component="p" variant="body2" className={classes.errorText}>
            {errors.gender.message || ''}
          </Typography>
        )}
        <ControlledDatePicker
          className={classes.input}
          label={t('exam->>NonNationalExam.DateOfBirth')}
          // value={dateOfBirthWatch || ''}
          onChange={(date) => {
            setValue('dateOfBirth', date || '');
            trigger('dateOfBirth');
          }}
          maxDate={dayjs()}
          disableFuture
          error={!!errors.dateOfBirth}
          helperText={errors.dateOfBirth?.message?.toString()}
          inputVariant="filled"
          isRequired
          autoOk
          control={control}
          name="dateOfBirth"
        />

        <ControlledAutocomplete
          className={classes.input}
          id="inputSelect-level"
          name="level"
          control={control}
          label={t('exam->>NonNationalExam.Level')}
          placeholder={t('exam->>PlaceHolders.EnterLevel')}
          options={levels || []}
          getOptionLabel={(option) => (typeof option === 'string' ? option : option.description)}
          error={!!errors?.level}
          helperText={errors?.level?.message?.toString()}
          isRequired
          disableClearable
          readOnly
        />
      </DialogContent>
      <DialogButtons
        apiButtonDisabled={false}
        apiButtonStatus="idle"
        onDialogClose={onDialogClose}
        apiButtonClick={apiButtonClick}
      />
    </Dialog>
  );
};

export default EditSiblingDialog;
